import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Convert VAPID public key from base64url to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated, token } = useAuth();
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [settings, setSettings] = useState({
    urgent_animals: true,
    followed_animals: true,
    post_comments: true,
    notification_radius: 1000
  });
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

  const authHeaders = useCallback(() => {
    const authToken = token || localStorage.getItem('taillink_token');
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }, [token]);

  // Check permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then(reg => {
          // Check if already subscribed
          reg.pushManager.getSubscription().then(sub => {
            setIsSubscribed(!!sub);
          });
        })
        .catch(err => console.log('SW registration failed:', err));
    }
  }, []);

  // Load settings when authenticated
  useEffect(() => {
    const authToken = token || localStorage.getItem('taillink_token');
    if (isAuthenticated && user && authToken) {
      loadNotificationSettings();
    } else {
      setIsSubscribed(false);
    }
  }, [isAuthenticated, user, token]);

  const loadNotificationSettings = async () => {
    const authToken = token || localStorage.getItem('taillink_token');
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/api/notifications/settings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSettings(response.data);
    } catch (error) {
      // Settings may not exist yet, use defaults
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      alert('เบราว์เซอร์นี้ไม่รองรับการแจ้งเตือน');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        await subscribeUser();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  };

  const subscribeUser = async () => {
    if (!isAuthenticated) return;
    if (!VAPID_PUBLIC_KEY) {
      console.warn('VAPID_PUBLIC_KEY not configured');
      return;
    }

    try {
      setLoading(true);
      
      const registration = await navigator.serviceWorker.ready;
      
      // Check existing subscription
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create real push subscription with VAPID key
        const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
      }

      // Extract keys from real subscription
      const subJSON = subscription.toJSON();
      
      // Send to backend
      await axios.post(`${API}/api/notifications/subscribe`, {
        endpoint: subJSON.endpoint,
        keys: {
          p256dh: subJSON.keys.p256dh,
          auth: subJSON.keys.auth
        }
      }, { headers: authHeaders() });

      setIsSubscribed(true);
    } catch (error) {
      console.error('Subscribe error:', error);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    try {
      setLoading(true);
      
      await axios.delete(`${API}/api/notifications/unsubscribe`, {
        headers: authHeaders()
      });
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      setIsSubscribed(false);
    } catch (error) {
      console.error('Unsubscribe error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/api/notifications/settings`, newSettings, {
        headers: authHeaders()
      });
      setSettings(prev => ({ ...prev, ...newSettings }));
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  const sendTestNotification = async () => {
    if (!isSubscribed || !isAuthenticated) {
      // Fallback to local notification
      if (permission === 'granted') {
        new Notification('🐾 TailLink Test', {
          body: 'การแจ้งเตือนทำงานปกติ!',
          icon: '/icon-192.png'
        });
      }
      return;
    }
    
    try {
      await axios.post(`${API}/api/notifications/test`, {}, {
        headers: authHeaders()
      });
    } catch (error) {
      // Fallback to local
      if (permission === 'granted') {
        new Notification('🐾 TailLink Test', {
          body: 'การแจ้งเตือนทำงานปกติ! (local)',
          icon: '/icon-192.png'
        });
      }
    }
  };

  const value = {
    permission,
    isSubscribed,
    settings,
    loading,
    requestPermission,
    subscribeUser,
    unsubscribe,
    updateSettings,
    sendTestNotification,
    loadNotificationSettings
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
